export class HttpError extends Error {
  constructor(public status: number, message: string, public payload?: unknown) {
    super(message);
  }
}

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
  });
  if (!res.ok) {
    let payload: unknown;
    try { payload = await res.json(); } catch { /* ignore */ }
    throw new HttpError(res.status, `${res.status} ${res.statusText}`, payload);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export interface AuthUser {
  id: number;
  email: string;
  name: string | null;
  role: 'admin' | 'manager' | 'warehouse' | 'viewer';
}

export interface UserRow extends AuthUser {
  active: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface OzonSettings {
  clientId: string;
  apiKeyMasked: string;
  hasApiKey: boolean;
  premium: boolean;
  verifiedAt: string | null;
  lastError: string | null;
  updatedAt: string | null;
}

export interface ProductsQuery {
  q?: string;
  limit?: number;
  offset?: number;
  sort?: 'name' | 'price' | 'oldPrice' | 'updated' | 'brand' | 'rrp' | 'stock' | 'offerId';
  dir?: 'asc' | 'desc';
  brand?: string;
  category?: string;
  stock?: 'in' | 'out';
  rrp?: 'set' | 'missing' | 'violation';
  rrpEnforce?: 'on' | 'off';
  ozonDiscount?: 'has' | 'none';
  visible?: 'true' | 'false';
  colorIndex?: 'WITHOUT_INDEX' | 'GREEN' | 'YELLOW' | 'RED';
  priceMin?: number;
  priceMax?: number;
  tireSize?: string;
  tireSeason?: string;
  diskSize?: string;
  diskPcd?: string;
}

function buildProductsQuery(params: ProductsQuery): URLSearchParams {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  return sp;
}

export interface ProductDetail {
  productId: string;
  offerId: string;
  sku: string | null;
  name: string;
  barcode: string | null;
  brand: string | null;
  brandOfficial: string | null;
  category: string | null;
  model: string | null;
  primaryImage: string | null;
  status: string | null;
  visible: boolean;
  archived: boolean;
  tireSize: string | null;
  tireSeason: string | null;
  diskSize: string | null;
  diskPcd: string | null;
  diskEt: string | null;
  diskDia: string | null;
  diskColor: string | null;
  rrpPrice: string | null;
  rrpUpdatedAt: string | null;
  rrpEnforceEnabled: boolean;
  rrpViolation: { effective: number; rrp: number; gap: number; gapPct: number } | null;
  attributes: { id: number; name: string | null; values: string[] }[] | null;
  attributesUpdatedAt: string | null;
  priceNow: {
    price: string;
    oldPrice: string | null;
    minPrice: string | null;
    marketingPrice: string | null;
    marketingSellerPrice: string | null;
    currencyCode: string;
    colorIndex: string | null;
    extPriceIndex: string | null;
    extMinPrice: string | null;
    ozonPriceIndex: string | null;
    ozonMinPrice: string | null;
    mpPriceIndex: string | null;
    mpMinPrice: string | null;
    commissions: Record<string, number> | null;
    autoActionEnabled: string | null;
    updatedAt: string;
  } | null;
  stocks: { warehouseId: string; type: string; present: number; reserved: number; updatedAt: string }[];
  priceHistory: { id: string; oldPrice: string | null; newPrice: string; source: string; strategyId: number | null; jobId: string | null; reason: string | null; appliedAt: string }[];
  strategies: { strategyId: number; strategyName: string; enabled: boolean; minPrice: string | null; maxPrice: string | null }[];
  createdAt: string;
  updatedAt: string;
}

export interface ProductUpdatePayload {
  brandOfficial?: string | null;
  category?: string | null;
  model?: string | null;
  rrpPrice?: number | null;
  rrpEnforceEnabled?: boolean;
  tireSize?: string | null;
  tireSeason?: string | null;
  diskSize?: string | null;
  diskPcd?: string | null;
  diskEt?: string | null;
  diskDia?: string | null;
  diskColor?: string | null;
}

export interface ProductFilters {
  brands: { value: string; count: number }[];
  categories: { value: string; count: number }[];
  tireSizes: { value: string; count: number }[];
  tireSeasons: { value: string; count: number }[];
  diskSizes: { value: string; count: number }[];
  diskPcds: { value: string; count: number }[];
  priceRange: { min: number; max: number };
}

export interface RepricersOverview {
  ourStrategies: {
    id: number;
    name: string;
    enabled: boolean;
    scheduleCron: string;
    productsCount: number;
    jobsCount: number;
    lastRun: { startedAt: string; finishedAt: string | null; status: string; productsTried: number; productsUpdated: number } | null;
  }[];
  rrp: {
    enabledProducts: number;
    availableProducts: number;
    lastRun: { startedAt: string; finishedAt: string | null; status: string; itemsProcessed: number } | null;
  };
  bullmq: {
    activeCount: number;
    waitingCount: number;
    delayedCount: number;
    active: { id: string; name: string; data: Record<string, unknown>; startedAt: string | null }[];
    repeatable: { id: string | null; name: string; pattern: string | null; next: string | null }[];
  };
  ozonStrategies: { id: string; name: string; type: string; enabled: boolean; products_count: number }[] | null;
}

export interface FinanceSummary {
  period: { from: string; to: string };
  operations: number;
  netPayout: number;
  grossRevenue: number;
  totalCommissions: number;
  totalDelivery: number;
  returnDelivery: number;
  byType: { type: string; count: number; amount: number; accruals: number; commission: number }[];
}

export interface FinanceTransaction {
  operationId: string;
  operationType: string;
  operationTypeName: string | null;
  operationDate: string;
  type: string | null;
  amount: string;
  accruals: string | null;
  saleCommission: string | null;
  deliveryCharge: string | null;
  returnDeliveryCharge: string | null;
  postingNumber: string | null;
  items: { name: string; sku: number }[] | null;
  services: { name: string; price: number }[] | null;
}

export interface PeriodStats { orders: number; revenue: number; commission: number; payout: number }

export interface DashboardKpis {
  today: PeriodStats;
  yesterday: PeriodStats;
  week: PeriodStats;
  prevWeek: PeriodStats;
  month: PeriodStats;
  prevMonth: PeriodStats;
  sparkline: { day: string; revenue: number }[];
  topProducts: { sku: string; name: string; orders_count: number; revenue: number }[];
  alerts: {
    outOfStock: number;
    lowStock: number;
    rrpViolations: number;
    urgentShipments: number;
    totalAwaitingPackaging: number;
  };
}

export interface ReturnRow {
  id: string;
  postingNumber: string | null;
  status: string;
  returnReasonName: string | null;
  returnedAt: string | null;
  sku: string | null;
  productName: string | null;
  quantity: number;
  price: string | null;
  commission: string | null;
  aiCategory: string | null;
  aiCategoryLabel: string | null;
  aiReason: string | null;
}

export interface OzonActionItem {
  id: number;
  title: string;
  type: string;
  description?: string;
  dateStart: string;
  dateEnd: string;
  freezeDate?: string;
  isParticipating: boolean;
  potentialCount: number;
  participatingCount: number;
  bannedCount: number;
  discountType?: string;
  discountValue?: number;
  isActive: boolean;
  daysLeft: number;
}

export interface DashboardAiWidgets {
  executiveBriefing?: { text: string; generatedAt: string; model: string | null } | null;
  healthScore?: { total: number; sales: number; returns: number; inventory: number; reviews: number; generatedAt: string } | null;
  decisions?: { type: string; label: string; count: number; link: string }[];
  forecast?: { stockoutRisk: number; trendingUp: number; trendingDown: number };
  actionItems?: { type: string; title: string; count: number; link: string; priority: 'high' | 'medium' | 'low' }[];
  pricingAlerts?: { productId: string; name: string; ourPrice: number; mpMinPrice: number; offerId: string | null }[];
  topRiskSku?: { productId: string; name: string | null; offerId: string | null; score: number; topCategory: string | null }[];
  reviewDrafts?: { id: string; rating: number; text: string; aiDraft: string | null; sku: string }[];
  opportunities?: { productId: string; name: string | null; offerId: string | null; predicted7d: number; daysOfStock: number | null }[];
  stockoutWarnings?: { productId: string; name: string | null; offerId: string | null; predicted7d: number; daysOfStock: number | null; stockNow: number }[];
  incomingReturns?: { count: number; byCategory: { category: string; count: number }[] };
  pickingPriority?: { count: number; urgentCount: number };
  imageFlags?: { count: number };
  weeklyDigest?: { summary: string; highlights: { type: string; text: string }[]; generatedAt: string } | null;
  marketTrend?: { ourGrowthPct: number | null; ourYellowRed: number; ourGreen: number; competitorSummary: string | null };
}

export interface AiActionItem {
  id: number;
  title: string;
  actionType: string;
  description: string | null;
  dateStart: string;
  dateEnd: string;
  freezeDate: string | null;
  isParticipating: boolean;
  participatingCount: number;
  potentialCount: number;
  bannedCount: number;
  discountType: string | null;
  discountValue: number | null;
  aiVerdict: 'recommended' | 'caution' | 'avoid' | null;
  aiAnalysis: string | null;
  expectedMarginPct: number | null;
  expectedUpliftFactor: number | null;
  analyzedAt: string | null;
  syncedAt: string;
}

export interface CronScheduleRow {
  id: string;
  queue: string;
  jobName: string;
  pattern: string;
  enabled: boolean;
  label: string | null;
  updatedAt: string;
}

export interface RepricerRecentJob {
  kind: string;
  label: string;
  startedAt: string | null;
  finishedAt: string | null;
  status: string;
  tried?: number;
  updated?: number;
  skipped?: number;
  error?: string | null;
}

export interface ProductRow {
  productId: string;
  offerId: string;
  sku: string | null;
  name: string;
  brand: string | null;
  category: string | null;
  image: string | null;
  visible: boolean;
  archived: boolean;
  price: string | null;
  oldPrice: string | null;
  minPrice: string | null;
  marketingPrice: string | null;
  marketingSellerPrice: string | null;
  colorIndex: 'WITHOUT_INDEX' | 'GREEN' | 'YELLOW' | 'RED' | null;
  extPriceIndex: string | null;
  extMinPrice: string | null;
  ozonPriceIndex: string | null;
  ozonMinPrice: string | null;
  mpPriceIndex: string | null;
  mpMinPrice: string | null;
  rrpPrice: string | null;
  rrpUpdatedAt: string | null;
  rrpEnforceEnabled: boolean;
  rrpViolation: { effective: number; rrp: number; gap: number; gapPct: number } | null;
  // Атрибуты из /v4/product/info/attributes
  brandOfficial: string | null;
  model: string | null;
  tireSize: string | null;
  tireSeason: string | null;
  diskSize: string | null;
  diskPcd: string | null;
  diskEt: string | null;
  diskDia: string | null;
  diskColor: string | null;
  stockTotal: number;
  updatedAt: string;
}

export const api = {
  // ---- auth ----
  login: (email: string, password: string) =>
    http<{ user: AuthUser }>('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout: () => http<void>('/api/auth/logout', { method: 'POST' }),
  me: () => http<{ user: AuthUser }>('/api/auth/me'),

  // ---- users (admin) ----
  users: () => http<{ items: UserRow[] }>('/api/users'),
  userCreate: (body: { email: string; password: string; name?: string; role: 'admin' | 'manager' | 'warehouse' | 'viewer' }) =>
    http<UserRow>('/api/users', { method: 'POST', body: JSON.stringify(body) }),
  userUpdate: (id: number, body: Partial<{ name: string; role: 'admin' | 'manager' | 'warehouse' | 'viewer'; active: boolean; password: string }>) =>
    http<UserRow>(`/api/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  userDelete: (id: number) => http<void>(`/api/users/${id}`, { method: 'DELETE' }),

  // ---- settings ----
  ozonSettings: () => http<OzonSettings>('/api/settings/ozon'),
  ozonSettingsSave: (body: { clientId: string; apiKey?: string; premium: boolean }) =>
    http<OzonSettings>('/api/settings/ozon', { method: 'PUT', body: JSON.stringify(body) }),
  ozonSettingsTest: () => http<{ ok: boolean; totalProducts?: number; error?: string }>('/api/settings/ozon/test', { method: 'POST' }),

  // ---- existing ----
  health: () => http<{ status: string; db: string; ozon: { configured: boolean; premium: boolean; source: 'db' | 'env' | null } }>('/health/full'),
  products: (params: ProductsQuery = {}) => {
    const sp = buildProductsQuery(params);
    return http<{ total: number; items: ProductRow[] }>(`/api/products?${sp.toString()}`);
  },
  productFilters: () => http<ProductFilters>('/api/products/filters'),
  productGet: (productId: string) => http<ProductDetail>(`/api/products/${productId}`),
  productUpdate: (productId: string, body: ProductUpdatePayload) =>
    http<{ ok: boolean; productId: string }>(`/api/products/${productId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  productSetPrice: (productId: string, body: { price: number; oldPrice?: number; minPrice?: number; reason?: string }) =>
    http<{ ok: boolean; updated: boolean; errors?: { code: string; message: string }[] }>(`/api/products/${productId}/set-price`, { method: 'POST', body: JSON.stringify(body) }),
  productsExportUrl: (params: ProductsQuery = {}) => `/api/products/export.csv?${buildProductsQuery(params).toString()}`,
  productsBulkRrpEnforce: (body: { enabled: boolean; product_ids?: string[]; filters?: Record<string, string> }) =>
    http<{ ok: boolean; updated: number }>('/api/products/bulk/rrp-enforce', { method: 'POST', body: JSON.stringify(body) }),

  // ---- repricers overview ----
  repricersOverview: () => http<RepricersOverview>('/api/repricers/overview'),
  repricersRecentJobs: () => http<{ items: RepricerRecentJob[] }>('/api/repricers/recent-jobs'),

  // ---- returns + actions ----
  returnsList: (params: { status?: string; category?: string; limit?: number; offset?: number } = {}) => {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v != null) sp.set(k, String(v));
    return http<{ total: number; byCategory: { category: string; label: string; count: number }[]; byStatus: { status: string; count: number }[]; items: ReturnRow[] }>(`/api/returns?${sp.toString()}`);
  },
  returnsClusters: () =>
    http<{ items: { id: number; label: string; description: string | null; keywords: string[]; count: number; samples: string[]; trend: string | null; updatedAt: string }[] }>('/api/returns/clusters'),
  returnsClustersRecompute: () => http<{ ok: boolean; queued: string }>('/api/returns/clusters/recompute', { method: 'POST' }),

  // ---- AI ----
  aiReturnRisks: (params: { level?: 'low' | 'medium' | 'high'; limit?: number; offset?: number } = {}) => {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v != null) sp.set(k, String(v));
    return http<{
      total: number;
      summary: { level: string; count: number }[];
      items: {
        productId: string; offerId: string | null; name: string | null; image: string | null; brand: string | null;
        score: number; level: string;
        salesCount90d: number; returnsCount90d: number; returnRate: number | null;
        avgRating: number | null; negativeReviews: number;
        topCategories: { category: string; count: number }[];
        computedAt: string;
      }[];
    }>(`/api/ai/return-risks?${sp.toString()}`);
  },
  aiReturnRisksRecompute: () => http<{ ok: boolean; queued: string }>('/api/ai/return-risks/recompute', { method: 'POST' }),

  aiForecasts: (params: { filter?: 'all' | 'stockout' | 'low' | 'trending_up' | 'trending_down'; limit?: number } = {}) => {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v != null) sp.set(k, String(v));
    return http<{
      summary: { stockout: number; trendingUp: number; trendingDown: number; low: number; total: number };
      items: {
        productId: string; offerId: string | null; name: string | null; image: string | null; brand: string | null;
        avgDaily7: number; avgDaily30: number; predicted7d: number;
        stockNow: number; daysOfStock: number | null; stockoutRisk: boolean; trend: string;
        computedAt: string;
      }[];
    }>(`/api/ai/forecasts?${sp.toString()}`);
  },
  aiForecastsRecompute: () => http<{ ok: boolean; queued: string }>('/api/ai/forecasts/recompute', { method: 'POST' }),

  aiActions: () => http<{ items: AiActionItem[] }>('/api/ai/actions'),
  aiActionsRecompute: () => http<{ ok: boolean; queued: string }>('/api/ai/actions/recompute', { method: 'POST' }),

  aiCompetitorDigest: () => http<{
    latest: {
      id: number;
      kind: string;
      periodStart: string;
      periodEnd: string;
      summary: string;
      highlights: { type: string; text: string }[];
      model: string | null;
      createdAt: string;
    } | null;
    snapshotsCount: number;
    lastSnapshotAt: string | null;
  }>('/api/ai/competitor-digest'),
  aiCompetitorDigestRecompute: () => http<{ ok: boolean; queued: string }>('/api/ai/competitor-digest/recompute', { method: 'POST' }),

  // ---- Chat ----
  chatConversations: () => http<{ items: { id: string; title: string; messagesCount: number; createdAt: string; updatedAt: string }[] }>('/api/chat/conversations'),
  chatConversation: (id: string) => http<{
    id: string;
    title: string | null;
    messages: { id: string; role: string; content: string; toolName: string | null; toolCalls: unknown; durationMs: number | null; model: string | null; createdAt: string }[];
  }>(`/api/chat/conversations/${id}`),
  chatSend: (body: { conversationId?: string; message: string }) =>
    http<{ conversationId: string; assistant: string; durationMs: number; model: string }>('/api/chat/messages', { method: 'POST', body: JSON.stringify(body) }),
  chatDelete: (id: string) => http<{ ok: boolean }>(`/api/chat/conversations/${id}`, { method: 'DELETE' }),

  aiImageChecks: (params: { filter?: 'all' | 'failed' | 'passed'; limit?: number } = {}) => {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v != null) sp.set(k, String(v));
    return http<{
      summary: { passed: number; failed: number };
      items: {
        productId: string; offerId: string | null; name: string | null; brand: string | null;
        imageUrl: string; score: number; passed: boolean; issues: string[]; comment: string | null;
        model: string | null; checkedAt: string;
      }[];
    }>(`/api/ai/image-checks?${sp.toString()}`);
  },
  aiImageChecksRecompute: () => http<{ ok: boolean; queued: string }>('/api/ai/image-checks/recompute', { method: 'POST' }),

  // ---- Dashboard AI ----
  dashboardAiWidgets: () => http<DashboardAiWidgets>('/api/dashboard/ai-widgets'),
  dashboardAiRecompute: () => http<{ ok: boolean; queued: string }>('/api/dashboard/ai-widgets/recompute', { method: 'POST' }),
  actionsList: () => http<{ items: OzonActionItem[] }>('/api/actions'),

  // ---- finance + dashboard ----
  dashboardKpis: () => http<DashboardKpis>('/api/dashboard/kpis'),
  financeSummary: (from?: string, to?: string) => {
    const sp = new URLSearchParams();
    if (from) sp.set('from', from);
    if (to) sp.set('to', to);
    return http<FinanceSummary>(`/api/finance/summary?${sp.toString()}`);
  },
  financeByDay: (from?: string, to?: string) => {
    const sp = new URLSearchParams();
    if (from) sp.set('from', from);
    if (to) sp.set('to', to);
    return http<{ items: { day: string; revenue: number; commission: number; payout: number; orders: number }[] }>(`/api/finance/by-day?${sp.toString()}`);
  },
  financeTransactions: (params: { from?: string; to?: string; type?: string; posting?: string; limit?: number; offset?: number } = {}) => {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v != null) sp.set(k, String(v));
    return http<{ total: number; items: FinanceTransaction[] }>(`/api/finance/transactions?${sp.toString()}`);
  },
  financeTopProducts: (from?: string, to?: string, limit = 20) => {
    const sp = new URLSearchParams();
    if (from) sp.set('from', from);
    if (to) sp.set('to', to);
    sp.set('limit', String(limit));
    return http<{ items: { sku: string; name: string; orders_count: number; revenue: number; commission: number }[] }>(`/api/finance/top-products?${sp.toString()}`);
  },

  // ---- cron schedules ----
  schedules: () => http<{ items: CronScheduleRow[] }>('/api/schedules'),
  scheduleUpdate: (id: string, body: Partial<{ pattern: string; enabled: boolean; label: string }>) =>
    http<CronScheduleRow>(`/api/schedules/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  scheduleRunNow: (id: string) =>
    http<{ accepted: boolean; jobId: string; scheduleId: string }>(`/api/schedules/${encodeURIComponent(id)}/run-now`, { method: 'POST' }),
  // ---- strategies ----
  strategies: () => http<{ items: { id: number; name: string; enabled: boolean; scheduleCron: string; createdAt: string; _count: { products: number; jobs: number } }[] }>('/api/strategies'),
  strategyGet: (id: number) => http<StrategyDetail>(`/api/strategies/${id}`),
  strategyCreate: (body: StrategyCreatePayload) =>
    http<{ id: number }>('/api/strategies', { method: 'POST', body: JSON.stringify(body) }),
  strategyUpdate: (id: number, body: Partial<StrategyCreatePayload>) =>
    http<unknown>(`/api/strategies/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  strategyDelete: (id: number) => http<void>(`/api/strategies/${id}`, { method: 'DELETE' }),
  strategySetProducts: (id: number, products: { productId: string; minPrice?: number; maxPrice?: number }[]) =>
    http<{ ok: boolean; count: number }>(`/api/strategies/${id}/products`, { method: 'POST', body: JSON.stringify({ products }) }),
  strategyJobs: (id: number) => http<{ items: PriceJob[] }>(`/api/strategies/${id}/jobs`),
  strategyRun: (id: number, dryRun = false) =>
    http<{ accepted: boolean; jobId: string; strategyId: number; dryRun: boolean }>(`/api/strategies/${id}/run`, { method: 'POST', body: JSON.stringify({ dryRun }) }),
  strategyPreview: (id: number) =>
    http<{ jobId: string; productsTried: number; productsUpdated: number; productsSkipped: number; decisions: PreviewDecision[] }>(`/api/strategies/${id}/preview`, { method: 'POST' }),

  // ---- ozon pricing strategies (внешний репрайсер Ozon) ----
  ozonCompetitors: () => http<{ competitor: OzonCompetitor[]; total: number }>('/api/ozon/competitors?limit=50'),
  ozonStrategies: () => http<{ strategies: OzonStrategy[]; total: number }>('/api/ozon/pricing-strategies?limit=49'),
  ozonStrategyInfo: (id: string) => http<OzonStrategyInfo>(`/api/ozon/pricing-strategies/${id}`),
  ozonStrategyCreate: (body: OzonStrategyCreatePayload) => http<{ strategyId: string }>('/api/ozon/pricing-strategies', { method: 'POST', body: JSON.stringify(body) }),
  ozonStrategyUpdate: (id: string, body: OzonStrategyCreatePayload) => http<{ ok: boolean }>(`/api/ozon/pricing-strategies/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  ozonStrategyDelete: (id: string) => http<void>(`/api/ozon/pricing-strategies/${id}`, { method: 'DELETE' }),
  ozonStrategyToggle: (id: string, enabled: boolean) => http<{ ok: boolean }>(`/api/ozon/pricing-strategies/${id}/toggle`, { method: 'POST', body: JSON.stringify({ enabled }) }),
  ozonStrategyProducts: (id: string) => http<{ products: OzonStrategyProduct[]; total: number }>(`/api/ozon/pricing-strategies/${id}/products?limit=100`),
  ozonStrategyProductsAdd: (id: string, productIds: string[]) => http<{ errors: { code: string; error: string; product_id: number }[]; failed_product_count: number }>(`/api/ozon/pricing-strategies/${id}/products/add`, { method: 'POST', body: JSON.stringify({ product_ids: productIds }) }),
  ozonStrategyProductsDelete: (id: string, productIds: string[]) => http<{ ok: boolean }>(`/api/ozon/pricing-strategies/${id}/products/delete`, { method: 'POST', body: JSON.stringify({ product_ids: productIds }) }),

  // ---- FBS (отгрузки) ----
  fbsPostings: (status: string = 'awaiting_packaging') =>
    http<{ total: number; items: FbsPostingItem[]; hasNext: boolean }>(`/api/fbs/postings?status=${status}`),
  fbsPostingGet: (postingNumber: string) =>
    http<FbsPostingDetail>(`/api/fbs/postings/${postingNumber}`),
  fbsShip: (postingNumber: string, body: FbsShipPayload) =>
    http<{ ok: boolean; posting_number: string; ship_result: string; shipment_id: string }>(`/api/fbs/postings/${postingNumber}/ship`, { method: 'POST', body: JSON.stringify(body) }),
  fbsShipments: (limit = 50) =>
    http<{ total: number; items: ShipmentRow[] }>(`/api/fbs/shipments?limit=${limit}`),
  fbsResyncOneC: (shipmentId: string) =>
    http<{ ok: boolean }>(`/api/fbs/shipments/${shipmentId}/resync-1c`, { method: 'POST' }),

  // ---- Акты приёма-передачи ----
  fbsActCreate: (body: { containers_count: number; delivery_method_id: number; departure_date?: string }) =>
    http<{ ok: boolean; actId: number }>('/api/fbs/act/create', { method: 'POST', body: JSON.stringify(body) }),
  fbsActStatus: (id: number) =>
    http<{ id: number; status: string; added_to_act?: string[]; removed_from_act?: string[] }>(`/api/fbs/act/${id}/status`),
  fbsActsList: () => http<{ items: FbsActListItem[] }>('/api/fbs/acts'),
  fbsActPdfUrl: (id: number) => `/api/fbs/act/${id}/pdf`,
  fbsActBarcodeUrl: (id: number) => `/api/fbs/act/${id}/barcode`,
  fbsBulkLabelsUrl: (status: 'awaiting_deliver' | 'awaiting_packaging' = 'awaiting_deliver') =>
    `/api/fbs/labels/bulk?status=${status}`,

  // ---- RRP (protection) ----
  rrpSync: () => http<{ accepted: boolean; jobId: string }>('/api/rrp/sync', { method: 'POST' }),
  rrpEnforce: (dryRun = false) => http<{ accepted: boolean; jobId: string; dryRun: boolean }>('/api/rrp/enforce', { method: 'POST', body: JSON.stringify({ dryRun }) }),
  rrpPreview: () => http<RrpPreview>('/api/rrp/preview', { method: 'POST' }),

  // ---- sync ----
  syncRuns: (kind?: string) => http<{ items: { id: string; kind: string; status: string; startedAt: string; finishedAt: string | null; itemsProcessed: number; errorText: string | null }[] }>(`/api/sync/runs${kind ? `?kind=${kind}` : ''}`),
  triggerSync: (kind: 'products' | 'prices' | 'stocks' | 'rrp') =>
    http<{ accepted: boolean; kind: string; note?: string; jobId?: string }>(`/api/sync/${kind}/trigger`, { method: 'POST' }),
};

// ============ strategy types (mirror packages/shared) ============

export type MetricKey =
  | 'stock_total' | 'turnover_days' | 'ext_price_index' | 'ozon_price_index' | 'color_index'
  | 'ordered_units_7d' | 'revenue_7d' | 'hits_view_7d' | 'conv_tocart_7d' | 'position_category' | 'buyout_rate_30d';

export type CompareOp = 'lt' | 'lte' | 'gt' | 'gte' | 'eq' | 'neq';

export type Action =
  | { kind: 'set_price'; value: number }
  | { kind: 'increase_pct'; value: number }
  | { kind: 'decrease_pct'; value: number }
  | { kind: 'increase_abs'; value: number }
  | { kind: 'decrease_abs'; value: number }
  | { kind: 'match_ext_min'; offsetPct: number };

export interface Rule {
  name: string;
  when: { metric: MetricKey; op: CompareOp; value: number | string }[];
  then: Action;
}

export interface StrategyConfig {
  globalMinPrice?: number;
  globalMaxPrice?: number;
  cooldownMin: number;
  dryRun: boolean;
  enforceBreakeven: boolean;
  minMargin: number;
  rules: Rule[];
}

export interface StrategyCreatePayload {
  name: string;
  scheduleCron?: string;
  enabled?: boolean;
  config: StrategyConfig;
}

export interface StrategyDetail {
  id: number;
  name: string;
  enabled: boolean;
  scheduleCron: string;
  config: StrategyConfig;
  products: {
    strategyId: number;
    productId: string;
    minPrice: string | null;
    maxPrice: string | null;
    product: { productId: string; offerId: string; name: string };
  }[];
}

export interface PriceJob {
  id: string;
  strategyId: number;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  productsTried: number;
  productsUpdated: number;
  productsSkipped: number;
  errorText: string | null;
}

// ============ FBS types ============

export interface FbsPostingProductRef {
  product_id: number;
  offer_id: string;
  name: string;
  quantity: number;
  price?: string;
  mandatory_mark_required: boolean;
}

export interface FbsPostingItem {
  posting_number: string;
  order_number?: string;
  status: string;
  in_process_at?: string;
  shipment_date?: string;
  delivery_method_name?: string;
  products: FbsPostingProductRef[];
}

export interface FbsPostingDetail extends FbsPostingItem {
  addressee?: { name: string; phone?: string };
  barcodes?: { upper_barcode?: string; lower_barcode?: string };
}

export interface FbsShipPayload {
  packages: {
    products: {
      product_id: number;
      quantity: number;
      exemplars: { mandatory_mark: string }[];
    }[];
  }[];
}

export interface FbsActListItem {
  id: number;
  delivery_method_id?: number;
  delivery_method_name?: string;
  status: string;
  departure_date?: string;
  created_at?: string;
  containers_count?: number;
  is_act_presented?: boolean;
}

export interface ShipmentRow {
  id: string;
  posting_number: string;
  status: string;
  shipped_at: string | null;
  delivery_method: string | null;
  marks_count: number;
  onec_synced_at: string | null;
  onec_error: string | null;
  onec_document_id: string | null;
}

export interface RrpPreviewDecision {
  productId: string;
  offerId: string;
  rrp: number;
  effective: number;
  ourPrice: number;
  newPrice?: number;
  reason: string;
}

export interface RrpPreview {
  jobId: string;
  productsChecked: number;
  productsAdjusted: number;
  productsSkipped: number;
  decisions: RrpPreviewDecision[];
}

// ============ Ozon Pricing Strategy types ============

export interface OzonCompetitor {
  id: number;
  name: string;
}

export interface OzonStrategy {
  id: string;
  name: string;
  type: 'MIN_EXT_PRICE' | 'COMP_PRICE';
  update_type?: string;
  enabled: boolean;
  products_count: number;
  competitors_count?: number;
  updated_at?: string;
}

export interface OzonStrategyInfo {
  strategy_id: string;
  strategy_name: string;
  strategy_descr?: string;
  type: 'MIN_EXT_PRICE' | 'COMP_PRICE';
  enabled: boolean;
  competitors: { competitor_id: number; coefficient: number }[];
}

export interface OzonStrategyCreatePayload {
  strategy_name: string;
  strategy_descr?: string;
  enabled: boolean;
  competitors: { competitor_id: number; coefficient: number }[];
}

export interface OzonStrategyProduct {
  product_id: number;
  is_enabled: boolean;
  price?: string;
  price_downloaded?: boolean;
  strategy_id?: string;
  strategy_product_price?: string;
  product_min_price?: string;
}

export interface PreviewDecision {
  productId: string;
  offerId: string;
  decision:
    | { kind: 'no_change'; reason: string }
    | { kind: 'change'; newPrice: number; rule: string; rawTarget: number; reason: string }
    | { kind: 'skip_no_price'; reason: string }
    | { kind: 'skip_below_min'; rawTarget: number; floor: number; reason: string }
    | { kind: 'skip_above_max'; rawTarget: number; ceiling: number; reason: string };
}
